import type { CommissionRule, LaneCard, ProductTypeId } from './types';
import type { ProductTypeCondition, AccommodationCondition } from './types';

export type MirrorSyncContext = {
  getOptions: (pt: ProductTypeId) => { id: number; name: string }[];
  accommodationTypeByProductId: Map<number, number>;
  addOnTypeByProductId: Map<number, number>;
};

function stripMirrors(cards: LaneCard[]): LaneCard[] {
  return cards.filter((c) => !c.isMirror);
}

export function conditionNeedsOppositeMirror(
  condition: ProductTypeCondition | AccommodationCondition | undefined
): boolean {
  if (!condition) return false;
  if (condition.mode === 'ANY') return false;
  if (condition.mode === 'CHOOSE_TYPE' && 'typeId' in condition && condition.typeId != null && Number(condition.typeId) > 0)
    return true;
  if (condition.mode === 'SELECT' && 'selectedIds' in condition && condition.selectedIds.length > 0) return true;
  return false;
}

function complementIds(allIds: number[], selected: Set<number>): number[] {
  return allIds.filter((id) => !selected.has(id));
}

function buildSelectCondition(
  ids: number[],
  options: { id: number; name: string }[]
): ProductTypeCondition {
  const nameById = new Map(options.map((o) => [o.id, o.name]));
  return {
    mode: 'SELECT',
    selectedIds: ids,
    selectedNames: ids.map((id) => nameById.get(id) ?? String(id)),
  };
}

function summaryExcludeMirrorFromInclude(selectedNames: string[]): string {
  if (selectedNames.length === 0) return 'ANY, except (selection)';
  if (selectedNames.length === 1) return `ANY, except ${selectedNames[0]}`;
  return `ANY, except ${selectedNames.join(', ')}`;
}

function summaryIncludeMirrorFromExclude(complementNames: string[]): string {
  if (complementNames.length === 0) return 'Specify product';
  if (complementNames.length <= 5) return complementNames.join(', ');
  return `${complementNames.slice(0, 3).join(', ')} + ${complementNames.length - 3} more`;
}

function buildMirrorCard(
  userCard: LaneCard,
  sourceLane: 'include' | 'exclude',
  ctx: MirrorSyncContext
): LaneCard | null {
  const { productType, condition } = userCard;
  if (!condition || !conditionNeedsOppositeMirror(condition)) return null;

  const options = ctx.getOptions(productType);
  const allIds = options.map((o) => o.id);
  if (allIds.length === 0) return null;

  const targetLane: 'include' | 'exclude' = sourceLane === 'include' ? 'exclude' : 'include';

  if (condition.mode === 'SELECT' && 'selectedIds' in condition) {
    const selected = new Set(condition.selectedIds);
    const comp = complementIds(allIds, selected);
    if (comp.length === 0) return null;
    const cond = buildSelectCondition(comp, options);
    const summary =
      targetLane === 'exclude'
        ? summaryExcludeMirrorFromInclude(condition.selectedNames ?? [])
        : summaryIncludeMirrorFromExclude(cond.selectedNames ?? []);
    return {
      id: `mirror-${targetLane}-${userCard.id}`,
      productType,
      condition: cond,
      summary,
      isMirror: true,
      mirrorOfCardId: userCard.id,
    };
  }

  if (condition.mode === 'CHOOSE_TYPE' && 'typeId' in condition && condition.typeId != null && Number(condition.typeId) > 0) {
    const typeId = condition.typeId;
    const typeName = condition.typeName ?? 'selected type';

    if (targetLane === 'include') {
      return {
        id: `mirror-${targetLane}-${userCard.id}`,
        productType,
        condition: { mode: 'ANY', selectedIds: [] },
        summary: `ANY, except ${typeName}`,
        isMirror: true,
        mirrorOfCardId: userCard.id,
      };
    }

    let comp: number[];
    if (productType === 'Accommodation') {
      comp = allIds.filter((id) => ctx.accommodationTypeByProductId.get(id) !== typeId);
    } else if (productType === 'AddOn') {
      comp = allIds.filter((id) => ctx.addOnTypeByProductId.get(id) !== typeId);
    } else {
      return null;
    }
    if (comp.length === 0) return null;
    const cond = buildSelectCondition(comp, options);
    const summary = `ANY, except ${typeName}`;
    return {
      id: `mirror-${targetLane}-${userCard.id}`,
      productType,
      condition: cond,
      summary,
      isMirror: true,
      mirrorOfCardId: userCard.id,
    };
  }

  return null;
}

export function syncMirroredLaneCards(rule: CommissionRule, ctx: MirrorSyncContext): CommissionRule {
  const include = stripMirrors(rule.include);
  const exclude = stripMirrors(rule.exclude);

  const extraInclude: LaneCard[] = [];
  const extraExclude: LaneCard[] = [];

  for (const card of include) {
    const m = buildMirrorCard(card, 'include', ctx);
    if (m) extraExclude.push(m);
  }
  for (const card of exclude) {
    const m = buildMirrorCard(card, 'exclude', ctx);
    if (m) extraInclude.push(m);
  }

  let includeUser = include;
  const hasChooseTypeMirrorInclude = (pt: ProductTypeId) =>
    extraInclude.some(
      (c) =>
        c.productType === pt &&
        c.summary.startsWith('ANY, except') &&
        c.condition?.mode === 'ANY'
    );
  if (hasChooseTypeMirrorInclude('Accommodation')) {
    includeUser = includeUser.filter(
      (c) => !(c.productType === 'Accommodation' && c.condition?.mode === 'ANY')
    );
  }
  if (hasChooseTypeMirrorInclude('AddOn')) {
    includeUser = includeUser.filter((c) => !(c.productType === 'AddOn' && c.condition?.mode === 'ANY'));
  }

  return {
    ...rule,
    include: [...includeUser, ...extraInclude],
    exclude: [...exclude, ...extraExclude],
  };
}